namespace $ {

	export type $easyreels_user_recorder_facing = 'user' | 'environment' | ''

	export type $easyreels_user_recorder_device_kind = 'videoinput' | 'audioinput'

	const frame_timeout_ms = 5000

	const choice_keys: Record<$easyreels_user_recorder_device_kind, string> = {
		videoinput: 'easyreels_user_recorder_camera',
		audioinput: 'easyreels_user_recorder_mic',
	}

	export function $easyreels_user_recorder_stop_tracks(stream: MediaStream | null | undefined) {
		stream?.getTracks().forEach(track => track.stop())
	}

	export function $easyreels_user_recorder_live(stream: MediaStream | null | undefined) {
		const tracks = stream?.getTracks() ?? []
		return tracks.length > 0 && tracks.every(track => track.readyState === 'live')
	}

	export function $easyreels_user_recorder_facing_of(track: MediaStreamTrack | undefined): $easyreels_user_recorder_facing {
		const facing = track?.getSettings().facingMode
		if (facing === 'user' || facing === 'environment') return facing

		const label = track?.label.toLowerCase() ?? ''
		if (/back|rear|environment|зад/.test(label)) return 'environment'
		if (/front|user|facetime|перед/.test(label)) return 'user'

		return ''
	}

	export class $easyreels_user_recorder_media extends $mol_object {

		mic: MediaStream | null = null
		remembered: Record<'user' | 'environment', string> = { user: '', environment: '' }
		generation = 0
		audio_context: AudioContext | null = null
		meter_source: MediaStreamAudioSourceNode | null = null
		meter_frame = 0

		@$mol_mem
		camera(next?: MediaStream | null) {
			return next ?? null
		}

		@$mol_mem
		level(next?: number) {
			return next ?? 0
		}

		@$mol_mem
		devices(next?: readonly MediaDeviceInfo[]) {
			return next ?? []
		}

		@$mol_mem
		mic_id(next?: string) {
			return next ?? ''
		}

		devices_of(kind: $easyreels_user_recorder_device_kind) {
			return this.devices().filter(device => device.kind === kind)
		}

		cameras() {
			return this.devices_of('videoinput').length
		}

		camera_id() {
			return this.camera()?.getVideoTracks()[0]?.getSettings().deviceId ?? ''
		}

		choice(kind: $easyreels_user_recorder_device_kind, next?: string) {
			try {
				return $mol_state_local.value<string>(choice_keys[kind], next) ?? ''
			} catch {
				return ''
			}
		}

		preferred(kind: $easyreels_user_recorder_device_kind) {
			const id = this.choice(kind)
			const known = this.devices_of(kind).filter(device => device.deviceId)

			return !known.length || known.some(device => device.deviceId === id) ? id : ''
		}

		device_listener = () => void this.enumerate().catch(() => {})

		camera_ended(_track: MediaStreamTrack) {}

		facing() {
			return $easyreels_user_recorder_facing_of(this.camera()?.getVideoTracks()[0])
		}

		combined() {
			return new MediaStream([
				...(this.camera()?.getVideoTracks() ?? []),
				...(this.mic?.getAudioTracks() ?? []),
			])
		}

		ready() {
			return $easyreels_user_recorder_live(this.camera()) && $easyreels_user_recorder_live(this.mic)
		}

		ensure_audio() {
			if (!this.audio_context || this.audio_context.state === 'closed') this.audio_context = new AudioContext()
			this.audio_context.resume().catch(() => {})
		}

		constraints(device: string, facing: $easyreels_user_recorder_facing): MediaTrackConstraints {
			const size = { width: { ideal: 1280 }, height: { ideal: 720 } }
			if (device) return { deviceId: { exact: device }, ...size }
			if (facing) return { facingMode: { exact: facing }, ...size }

			return { facingMode: { ideal: 'user' }, ...size }
		}

		async warm(stream: MediaStream) {
			const video = Object.assign(document.createElement('video'), { muted: true, playsInline: true, srcObject: stream })
			await video.play()
			if (video.videoWidth) return

			await new Promise<void>((resolve, reject) => {
				const timer = setTimeout(() => reject(new $easyreels_i18n_error('The camera produced no frames', $easyreels_i18n_say('$easyreels_user_recorder_no_frames'))), frame_timeout_ms)
				video.onloadedmetadata = () => {
					clearTimeout(timer)
					resolve()
				}
			})
		}

		async acquire(facing: $easyreels_user_recorder_facing, device = '') {
			const remembered = !device && facing ? this.remembered[facing] : ''
			let stream: MediaStream

			try {
				stream = await navigator.mediaDevices.getUserMedia({ video: this.constraints(device || remembered, facing), audio: false })
			} catch (error) {
				if (!remembered || !facing) throw error
				this.remembered[facing] = ''
				stream = await navigator.mediaDevices.getUserMedia({ video: this.constraints('', facing), audio: false })
			}

			try {
				await this.warm(stream)
				const actual = $easyreels_user_recorder_facing_of(stream.getVideoTracks()[0])
				if (facing && actual && actual !== facing) throw new $easyreels_i18n_error(
					`The camera returned ${actual}, requested ${facing}`,
					$easyreels_i18n_say('$easyreels_user_recorder_wrong_camera'),
				)

				return stream
			} catch (error) {
				$easyreels_user_recorder_stop_tracks(stream)
				throw error
			}
		}

		show(next: MediaStream) {
			const track = next.getVideoTracks()[0]
			const facing = $easyreels_user_recorder_facing_of(track)
			const device = track.getSettings().deviceId ?? ''
			if (facing && device) this.remembered[facing] = device

			track.addEventListener('ended', () => this.camera_ended(track), { once: true })
			this.camera(next)
		}

		preview_constraints(camera: string, mic: string): MediaStreamConstraints {
			return { video: this.constraints(camera, ''), audio: mic ? { deviceId: { exact: mic } } : true }
		}

		async open_preview() {
			const generation = ++this.generation
			await this.enumerate()
			const camera = this.preferred('videoinput')
			const mic = this.preferred('audioinput')
			const constraints = this.preview_constraints(camera, mic)
			let stream: MediaStream

			try {
				stream = await navigator.mediaDevices.getUserMedia(constraints)
			} catch {
				$easyreels_user_recorder_stop_tracks(this.camera())
				$easyreels_user_recorder_stop_tracks(this.mic)
				try {
					stream = await navigator.mediaDevices.getUserMedia(constraints)
				} catch (error) {
					if (!camera && !mic) throw error
					stream = await navigator.mediaDevices.getUserMedia(this.preview_constraints('', ''))
				}
			}

			if (generation !== this.generation) {
				$easyreels_user_recorder_stop_tracks(stream)
				throw new $easyreels_i18n_error('The preview was superseded', $easyreels_i18n_say('$easyreels_user_recorder_superseded'))
			}

			$easyreels_user_recorder_stop_tracks(this.camera())
			$easyreels_user_recorder_stop_tracks(this.mic)
			this.hold_mic(new MediaStream(stream.getAudioTracks()), mic)
			this.show(new MediaStream(stream.getVideoTracks()))
			navigator.mediaDevices.addEventListener('devicechange', this.device_listener)
			await this.enumerate()
			this.meter_start()
		}

		hold_mic(next: MediaStream, requested: string) {
			this.mic = next
			this.mic_id(next.getAudioTracks()[0]?.getSettings().deviceId || requested)
		}

		async switch_preview(device = '') {
			const generation = ++this.generation
			const old = this.camera()
			const facing = device ? '' : this.facing() === 'environment' ? 'user' : 'environment'
			let next: MediaStream

			try {
				next = await this.acquire(facing, device)
			} catch {
				$easyreels_user_recorder_stop_tracks(old)
				next = await this.acquire(facing, device)
			}

			if (generation !== this.generation) {
				$easyreels_user_recorder_stop_tracks(next)
				return
			}

			$easyreels_user_recorder_stop_tracks(old)
			this.show(next)
		}

		async switch_mic(device: string) {
			const generation = ++this.generation
			const constraints = { audio: { deviceId: { exact: device } }, video: false }
			let next: MediaStream

			try {
				next = await navigator.mediaDevices.getUserMedia(constraints)
			} catch {
				$easyreels_user_recorder_stop_tracks(this.mic)
				next = await navigator.mediaDevices.getUserMedia(constraints)
			}

			if (generation !== this.generation) {
				$easyreels_user_recorder_stop_tracks(next)
				return
			}

			$easyreels_user_recorder_stop_tracks(this.mic)
			this.hold_mic(new MediaStream(next.getAudioTracks()), device)
			this.meter_start()
		}

		async enumerate() {
			this.devices(await navigator.mediaDevices.enumerateDevices())
		}

		meter_start() {
			const context = this.audio_context
			if (!context || !this.mic) return

			this.meter_stop()
			const source = context.createMediaStreamSource(this.mic)
			const analyser = context.createAnalyser()
			analyser.fftSize = 512
			source.connect(analyser)
			this.meter_source = source

			const samples = new Uint8Array(analyser.fftSize)
			const draw = () => {
				analyser.getByteTimeDomainData(samples)
				let sum = 0
				for (const sample of samples) sum += ((sample - 128) / 128) ** 2
				this.level(Math.min(1, Math.sqrt(sum / samples.length) * 4))
				this.meter_frame = requestAnimationFrame(draw)
			}
			draw()
		}

		meter_stop() {
			cancelAnimationFrame(this.meter_frame)
			this.meter_source?.disconnect()
			this.meter_source = null
			this.level(0)
		}

		release() {
			this.generation++
			this.meter_stop()
			navigator.mediaDevices?.removeEventListener('devicechange', this.device_listener)
			$easyreels_user_recorder_stop_tracks(this.camera())
			$easyreels_user_recorder_stop_tracks(this.mic)
			this.mic = null
			this.mic_id('')
			this.camera(null)
		}

		override destructor() {
			this.release()
			this.audio_context?.close().catch(() => {})
		}

	}

}
